import React, { useState, useEffect, useMemo, useCallback } from 'react';
import _ from 'lodash';

import { Button } from 'components/commons';
import ButtonGroup from 'components/button-group';
import Dropdown from 'components/dropdown';
import Loading from 'components/loading';
import InputText from 'components/inputtext';
import Title from 'components/title';
import {
    useGetBanksQuery,
    LoanApplication,
    Borrower,
    BankAccount,
    useSetBorrowerBankAccountMutation,
    useGetLoanQuery
} from 'generated/graphql';
import { validateObject, sanitizeString, getLoanApplicationParams } from 'utils/utils';
import { useNavigation } from 'hooks/index';
import { getFieldOrder } from 'configs/routes';
import { CONFIG, ESTIMULO_2020 } from 'constants/constants';
import { useProfile } from 'providers/profile.provider';
import { useProduct } from 'providers/product.provider';

const filterOptions = (select, filter) => {
    const sanitizedFilter = sanitizeString(filter);
    if (sanitizedFilter) {
        const sanitizedName = sanitizeString(select.data.name);
        return sanitizedName.includes(sanitizedFilter) || select.data.code.includes(sanitizedFilter);
    }
    return true;
};

const BankAccountConfiguration: React.FC = () => {
    const [setBorrowerBankAccount] = useSetBorrowerBankAccountMutation();
    const [showValidation, setShowValidation] = useState(false);
    const [state, setState] = useState({ submitting: false });
    const [invalidFields, setInvalidFields] = useState([]);

    const { goToPreviousRoute, nextScreen } = useNavigation();
    const { loanApplicationUtmSource } = useProfile();
    const { allowedBanks } = useProduct();
    const loanApplicationId  = getLoanApplicationParams();

    const { data:bankData } = useGetBanksQuery();

    const {data,loading } = useGetLoanQuery({
        variables:{ id: loanApplicationId},
        fetchPolicy:"cache-first"
    });

    const loanApplication = _.get(data, 'loanApplication', {} as LoanApplication);
    const borrower = _.get(loanApplication, 'borrower',{} as Borrower);
    const bankAccount = _.get(borrower, 'bankAccount',{} as BankAccount);

    const [form, setForm] = useState({
        ...bankAccount,
        verificationDigit: ""
    });


    const initializeForm = () => {
        const [account, verificationDigit] = (bankAccount.account || '').split('-');

        const newForm = {
            ...bankAccount,
            account,
            verificationDigit,
        };

        setForm(newForm);
        setInvalidFields(validateObject(newForm));
    }

    const validateForSubmit = () => {
        if (state.submitting) {
            setInvalidFields(validateObject(form));
            setShowValidation(true);
        }
    }

    const submit = () => {
        if (state.submitting && !invalidFields.length) {
            setBorrowerBankAccount({
                variables: {
                    loanApplicationId: loanApplication.id,
                    bankAccount: {
                        agency: form.agency,
                        account: `${form.account}-${form.verificationDigit}`,
                        bankId: form.bank.id
                    }
                }
            });

            nextScreen();
        }
        setState({ ...state, submitting: false });
    }

    useEffect(initializeForm, [bankAccount]);
    useEffect(validateForSubmit, [state.submitting]);
    useEffect(submit, [invalidFields]);

    const findError = (field: string) => invalidFields.find(invalidField => invalidField === field) && showValidation ? "ui-error" : "";

    const updateForm = (variableName: string, value: any, fieldName: string = variableName) => {
        if (value) {
            _.remove(invalidFields, invalidField => invalidField === fieldName);
        } else {
            setInvalidFields(validateObject({
                ...form,
                [variableName]: value
            }));
        }

        setForm({
            ...form,
            [variableName]: value
        });
    }

    const possibleBanks = useMemo(() => CONFIG.BANK.AVAILABLE_OPTIONS(allowedBanks || bankData?.banks), [bankData?.banks, CONFIG.BANK.AVAILABLE_OPTIONS]);
    const updateField = useCallback((e) => updateForm(e.currentTarget.name, e.currentTarget.value), [updateForm]);
    const formValue = (value: any) => (_.isEmpty(value) ? '' : value);

    if (loading || _.isEmpty(form)) {
        return (
            <Loading />
        );
    }

    const components = getFieldOrder([
        {
            id:"bank",
            render: (key: number) =>
                <Dropdown
                    value={form.bank && form.bank.code ? form.bank : ''}
                    className={`grid-bank-field ${findError('bank')}`}
                    options={possibleBanks} placeholder="Banco"
                    formatOptionLabel={(b) => b.code ? `${b.code} - ${b.name}` : 'Banco'}
                    filterOption={filterOptions} id="bank"
                    controlShouldRenderValue={form.bank && form.bank.code}
                    getOptionValue={(b) => b.code}
                    onChange={(e) => updateForm("bank", e)}
                    key={key}
                />
        },
        {
            id:"account",
            render: (key: number) =>
                <React.Fragment key={key}>
                    <InputText
                        value={formValue(form.agency)}
                        id="agency"
                        className={`grid-agency-field ${findError('agency')}`}
                        label="Agência"
                        type="tel"
                        maxLength={4}
                        keyfilter={/^[0-9]*$/}
                        onChange={updateField}
                    />

                    <div className="grid-account-field">
                        <InputText
                            value={formValue(form.account)}
                            id="account"
                            className={`${findError('account')}`}
                            label="Conta corrente"
                            type="tel"
                            keyfilter={/^[0-9]*$/}
                            onChange={updateField}
                        />

                        <span className="dash-verification-digit" />
                    </div>

                    <InputText
                        value={formValue(form.verificationDigit)}
                        id="verificationDigit"
                        className={`grid-verification-digit-field ${findError('verificationDigit')}`}
                        label="Dígito"
                        type="tel"
                        keyfilter={/^[0-9]*$/}
                        onChange={updateField}
                    />
                </React.Fragment>
        }
    ], "BankAccountConfiguration");

    return (
        <div className="bankaccount-page">
            <Title>agora, a conta bancária da empresa</Title>

            {loanApplicationUtmSource === ESTIMULO_2020 &&
                <p className="grid-sub-title pb-4">
                    Para sua segurança, depositamos o valor do empréstimo na conta PJ da sua empresa, nos principais bancos do país:
                </p>
            }

            { components.map((comp, key) => comp.render(key)) }

            <ButtonGroup className="buttons-group-mobile">
                <Button
                    label="voltar"
                    variant="secondary"
                    type="button"
                    onClick={goToPreviousRoute}
                />

                <Button
                    label="continuar"
                    disabled={invalidFields.length > 0}
                    onClick={async () => setState({ ...state, submitting: true })}
                />
            </ButtonGroup>

        </div>
    );
}

export default BankAccountConfiguration;
